<template>
  <v-flex xs12>
    <v-card>
      <v-layout row wrap justify-space-between class="px-2 pt-3">
        <v-form v-model="valid">
          <div class="layout row wrap">
            <v-select
              class="min-w mx-3"
              :items="months"
              :label="$lang.actions.selectMonth"
              v-model="time.month"
              required
              :rules="[v => !!v || `${$lang.errors.missingMonth}`]"
            />
            <v-select
              class="min-w mx-3"
              :items="years"
              :label="$lang.actions.selectYear"
              v-model="time.year"
              required
              :rules="[v => !!v || `${$lang.errors.missingYear}`]"
            />
            <v-btn class="primary mx-2 my-2" @click="showReports()" :disabled="!valid">{{$lang.actions.showReports}}</v-btn>
            <v-btn class="primary mx-2 my-2" @click="showOverview()" :disabled="reports.length === 0">{{!showOverviewData ? $lang.actions.showPaymentOverview : $lang.actions.hidePaymentOverview}}</v-btn>
          </div>
        </v-form>
        <v-text-field
          v-if="show"
          class="min-w max-w mx-3"
          v-model="search"
          append-icon="search"
          :label="$lang.labels.search"
        />
      </v-layout>
      <v-flex xs12 px-4 pb-3 v-if="err">
        <v-alert type="error" :value="true" class="my-0">{{err}}</v-alert>
      </v-flex>
      <v-data-table
        v-if="show"
        :headers="headers"
        :items="reports"
        :search="search"
        :loading="loading"
        :rows-per-page-items="[50, 100, 250, 500, { text:'All' , value: -1 }]"
      >
        <v-progress-linear height="6" slot="progress" color="primary" indeterminate />
        <template slot="items" slot-scope="props">
          <td>{{ props.item.type }}</td>
          <td class="min-w">{{ props.item.companyName }}</td>
          <td>{{ formatDate(props.item.createdOn) }}</td>
          <td>{{ months[parseInt(props.item.month, 10) - 1].text }}</td>
          <td>{{ props.item.year }}</td>
          <td v-if="showOverviewData">
            <v-layout row wrap>
              <v-flex xs6>
                <span class="text-xs-left">{{ $lang.labels.webshopOrderNo }}: {{ (calculateOrderData(props.item.webShopResources, 'webshop')).noOfOrders }}</span>
                <br>
                <span class="text-xs-left">{{ $lang.labels.appOrderNo }}: {{ (calculateOrderData(props.item.webShopResources, 'ordering app')).noOfOrders }}</span>
              </v-flex>
              <v-flex xs6>
                <template v-for="type in calculatePaymentSummary(props.item.webShopResources.map(x => x.paymentSummary).flat(Infinity))">
                  <span class="text-xs-left">{{ $lang.labels[type.paymentType] }}: {{ type.amount }}</span><br>
                </template>
                <span class="text-xs-left">{{ $lang.labels.webshopTotal }}: {{ (calculateOrderData(props.item.webShopResources, 'webshop')).ordersSum }}</span>
                <br>
                <span class="text-xs-left">{{ $lang.labels.appTotal }}: {{ (calculateOrderData(props.item.webShopResources, 'ordering app')).ordersSum }}</span>
              </v-flex>
            </v-layout>
          </td>
          <td>
            <v-btn class="primary mx-0" @click="showReportDetails(props.item)">{{$lang.actions.view}}</v-btn>
          </td>
        </template>
        <v-alert slot="no-results" :value="true" color="warning" icon="warning">
          Your search for "{{ search }}" found no results.
        </v-alert>
      </v-data-table>
    </v-card>
    <v-dialog v-if="reportDetailsModal" v-model="reportDetailsModal" max-width="1200">
      <report-details-modal :reportDetails="reportDetails" @close="reportDetailsModal = false"/>
    </v-dialog>
  </v-flex>
</template>

<script>
import {DateTime} from 'luxon';

export default {
    components: {
      reportDetailsModal: () => import('../modals/WebshopReportsModal'),
    },
    data() {
      return {
        err: '',
        search: '',
        valid: false,
        show: false,
        loading: false,
        reports: [],
        headers: [
          { text: this.$lang.labels.type, value: 'type', align: 'left', },
          { text: this.$lang.labels.name, value: 'companyName', align: 'left', },
          { text: this.$lang.labels.created, value: 'createdOn', align: 'left', },
          { text: this.$lang.labels.month, value: 'month', align: 'left', },
          { text: this.$lang.labels.year, value: 'year', align: 'left', },
          { text: this.$lang.labels.actions, value: 'action', align: 'left', sortable: false, },
        ],
        time: {
          month: DateTime.local().month.toString(),
          year: DateTime.local().year.toString(),
        },
        years: [],
        months: [
          {value: '1', text: this.$lang.labels.january},
          {value: '2', text: this.$lang.labels.february},
          {value: '3', text: this.$lang.labels.march},
          {value: '4', text: this.$lang.labels.april},
          {value: '5', text: this.$lang.labels.may},
          {value: '6', text: this.$lang.labels.june},
          {value: '7', text: this.$lang.labels.july},
          {value: '8', text: this.$lang.labels.august},
          {value: '9', text: this.$lang.labels.september},
          {value: '10', text: this.$lang.labels.october},
          {value: '11', text: this.$lang.labels.november},
          {value: '12', text: this.$lang.labels.december},
        ],
        reportDetailsModal: false,
        reportDetails: null,
        showOverviewData: false,
      };
    },
    watch: {
      err(val) {
        if (val !== '') {
          setTimeout(() => { this.err = ''; }, 5000);
        }
      },
    },
    created() {
      const yearNow = (new Date()).getFullYear();
      for (let i = 2018; i <= yearNow; i += 1) {
        this.years.push(i.toString());
      }
    },
    methods: {
      formatDate(date) {
        return DateTime.fromISO(date).toFormat('yyyy-MM-dd');
      },
      showOverview() {
        if (!this.showOverviewData) {
          this.headers.splice(5, 0, { text: this.$lang.header.paymentSummary, value: 'paymentOverview', align: 'left', });
          this.showOverviewData = true;
        } else {
          this.headers.splice(5, 1);
          this.showOverviewData = false;
        }
      },
      calculatePaymentSummary(report) {
        const helper = {};
        return report.reduce(function (r, o) {
          const key = o.paymentType;

          if (!helper[key]) {
            helper[key] = Object.assign({}, o);
            r.push(helper[key]);
          } else {
            helper[key].amount += o.amount;
          }

          return r;
        }, []);
      },
      calculateOrderData(report, type) {
        const reportMap = report.map(x => {
          if (x.ordersData && x.ordersData[type]) return x.ordersData[type];
          return {noOfOrders: 0, ordersSum: 0};
        });
        return reportMap.reduce((a, b) => {
          for (let k in b) {
            if (b.hasOwnProperty(k))
              a[k] = (a[k] || 0) + b[k];
          }
          return a;
        }, {});
      },
      showReportDetails(report) {
        this.reportDetails = {
          id: report.id,
        }
        this.reportDetailsModal = true;
      },
      showReports() {
        this.loading = true;
        this.show = true;
        this.$http.post('report/getall' , this.time)
          .then((res) => {
            this.loading = false;
            if (res.body.status !== 'Success') {
              this.err = res.body.statusText;
              return;
            }
            this.reports = res.body.reports;
          }, (err) => {
            this.loading = false;
            this.err = err.body.statusText || err.body.message;
          });
      },
    },
  };
</script>



// WEBPACK FOOTER //
// WebshopReports.vue?3002757a